import getReducerKeyProxy from './proxy'
import configureStore from './configurestore'

export const getStateOperations = (store, reducerKey, initialState, module) => {
  if (process.env.NODE_ENV !== 'production') {
    if (store === undefined) {
      throw new Error('getStateOperations: store must be defined.')
    }
    if (typeof store.getState !== 'function') {
      throw new Error('getStateOperations: The store argument must be a redux store.')
    }
    if (typeof reducerKey !== 'string') {
      throw new Error('getStateOperations: reducerKey must be defined and be a string.')
    }
  }
  // Get the 3 basic state operations.
  const retObj = {
    getState: () => store.getRState(reducerKey),
    setState: (objToMerge, type) => store.setRState(reducerKey, objToMerge, type),
    reducerState: initialState !== undefined ? getReducerKeyProxy(store, reducerKey, initialState) : {}
  }
  /* istanbul ignore if */
  if (module && module.hot) {
    module.hot.dispose(data => {
      data.operations = { ...retObj }
    })
  }
  return retObj
}

export const handleHMR = (module, initialState) => {
  let setState = () => {}
  let getState = initialState !== undefined ? () => initialState : () => ({})
  let reducerState = initialState !== undefined ? { ...initialState } : {}
  /* istanbul ignore if */
  if (module.hot) {
    if (
      typeof module.hot.data !== 'undefined' &&
      typeof module.hot.data.operations !== 'undefined'
    ) {
      // eslint-disable-next-line
      ({ setState, getState, reducerState } = module.hot.data.operations)
      // Save the store into module.hot.data.store before unloading this module
      module.hot.dispose(data => {
        if (data === undefined) {
          module.hot.data = { operations: { setState, getState, reducerState } }
        } else {
          data.operations = { setState, getState, reducerState }
        }
      })
    }
  }

  return { setState, getState, reducerState }
}

// Creates general purpose module data under a redux reducerKey.
// A redux store must be imported from your redux createStore module for the argument below.
export const createReduxData = (store, reducerKey, initialState) => {
  if (process.env.NODE_ENV !== 'production') {
    if (store === undefined) {
      throw new Error('The first parameter (store) to createReduxData must be defined.')
    }
    if (typeof reducerKey !== 'string') {
      throw new Error('The second parameter (reducerKey) to createReduxData must a string.')
    }
    if (initialState === undefined) {
      throw new Error('The third parameter (initialState) to createReduxData must be defined.')
    }
  }
  store.addReducer(store, reducerKey, initialState)
  return getStateOperations(store, reducerKey, initialState)
}

export const createReduxDataAndStore = (reducerKey, initialState, reduxEnhancer) => {
  if (typeof reducerKey !== 'string') {
    throw new Error('The first parameter (reducerKey) to createReduxData must a string.')
  }
  if (initialState === undefined) {
    throw new Error('The second parameter (initialState) to createReduxData must be defined.')
  }

  const store = configureStore(undefined, undefined, reduxEnhancer)
  const { setState, getState, reducerState } = createReduxData(store, reducerKey, initialState)
  return {
    store,
    setState,
    getState, 
    reducerState
  }
}

export const getReducerState = (state, reducerKey) =>
  state[reducerKey.toString()] !== undefined ? state[reducerKey.toString()] : {}

export const handleOnConstructor = (options, store) => {
  if (
    options === undefined ||
    typeof options.onConstructor !== 'function' ||
    options.reducerKey === undefined ||
    options.initialState === undefined
  ) {
    return
  }
  store.addReducer(store, options.reducerKey, options.initialState)
  const result = getStateOperations(store, options.reducerKey, options.initialState, options.module)
  result.store = store
  options.onConstructor(result)
  return result
}

/*
store - redux store.
options = {
  // Reset the state to the initial state when the component is re-constructed.
  // Otherwise, the state persists across component constructions.
  autoReset: boolean
  // redux reducer key
  reducerKey: string
  // Initial state object. The state will be set to this on the first constuction of the component.
  // If autoReset is true, it will be set to then initial state whenever the component is constructed.
  initialState: object
  // module variable available in any module file.
  module: object
}
*/
export const handleOnConnect = (store, options) => {
  if (process.env.NODE_ENV !== 'production') {
    if (options === undefined) {
      throw new Error('handleOnConnect: options cannot be undefined.')
    }
    if (options.reducerKey === undefined) {
      throw new Error('handleOnConnect: reducerKey cannot be undefined.')
    }
    if (options.initialState === undefined) {
      throw new Error('handleOnConnect: initialState cannot be undefined.')
    }
  }
  store.addReducer(store, options.reducerKey, options.initialState)
  const result = getStateOperations(store, options.reducerKey, options.initialState, options.module)
  result.store = store
  if (options.autoReset) {
    result.setState({ ...options.initialState })
  }
  return result
}

